/**
 * Custom storage adapter for Supabase Auth using IndexedDB
 *
 * IndexedDB is more persistent than localStorage on mobile browsers,
 * especially Chrome mobile which aggressively clears localStorage for PWAs.
 *
 * This adapter uses idb-keyval for a simple key-value interface to IndexedDB,
 * with localStorage as a synchronous fallback for initial reads.
 */

import { get, set, del } from 'idb-keyval';

// Storage key prefix to avoid collisions
const STORAGE_PREFIX = 'supabase-auth-';

/**
 * IndexedDB-based storage adapter for Supabase Auth
 * Implements the same interface as localStorage but uses IndexedDB for persistence
 */
export const indexedDbStorage = {
  /**
   * Get item from storage
   * Tries localStorage first for synchronous access (Supabase needs this on init),
   * then syncs from IndexedDB in background
   */
  getItem: async (key: string): Promise<string | null> => {
    const prefixedKey = STORAGE_PREFIX + key;

    // Try localStorage first (synchronous, needed for initial auth check)
    const localValue = localStorage.getItem(prefixedKey);

    // Also check IndexedDB (more persistent)
    try {
      const idbValue = await get(prefixedKey);

      if (idbValue && !localValue) {
        // IndexedDB has value but localStorage doesn't - restore to localStorage
        localStorage.setItem(prefixedKey, idbValue);
        return idbValue;
      }

      if (localValue && !idbValue) {
        // localStorage has value but IndexedDB doesn't - sync to IndexedDB
        await set(prefixedKey, localValue);
      }

      return idbValue || localValue;
    } catch (error) {
      console.warn('[IndexedDB Storage] Failed to read from IndexedDB, using localStorage:', error);
      return localValue;
    }
  },

  /**
   * Set item in storage
   * Writes to both localStorage and IndexedDB for redundancy
   */
  setItem: async (key: string, value: string): Promise<void> => {
    const prefixedKey = STORAGE_PREFIX + key;

    // Always write to localStorage (synchronous backup)
    localStorage.setItem(prefixedKey, value);

    // Also write to IndexedDB (more persistent)
    try {
      await set(prefixedKey, value);
    } catch (error) {
      console.warn('[IndexedDB Storage] Failed to write to IndexedDB:', error);
    }
  },

  /**
   * Remove item from storage
   * Removes from both localStorage and IndexedDB
   */
  removeItem: async (key: string): Promise<void> => {
    const prefixedKey = STORAGE_PREFIX + key;

    // Remove from localStorage
    localStorage.removeItem(prefixedKey);

    // Remove from IndexedDB
    try {
      await del(prefixedKey);
    } catch (error) {
      console.warn('[IndexedDB Storage] Failed to delete from IndexedDB:', error);
    }
  },
};

/**
 * Migrate existing Supabase session from default localStorage key to new prefixed key
 * This ensures users don't get logged out after the storage migration
 */
export async function migrateExistingSession(supabaseUrl: string): Promise<void> {
  // Supabase stores sessions with key pattern: sb-{project-ref}-auth-token
  // Extract project ref from URL
  const urlMatch = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/);
  if (!urlMatch) return;

  const projectRef = urlMatch[1];
  const oldKey = `sb-${projectRef}-auth-token`;
  const newKey = STORAGE_PREFIX + oldKey;

  // Check if old key exists and new key doesn't
  const oldValue = localStorage.getItem(oldKey);
  const newValue = localStorage.getItem(newKey);

  if (oldValue && !newValue) {
    console.log('[IndexedDB Storage] Migrating existing session to new storage');
    localStorage.setItem(newKey, oldValue);
    try {
      await set(newKey, oldValue);
    } catch (error) {
      console.warn('[IndexedDB Storage] Failed to migrate to IndexedDB:', error);
    }
  }
}
